import { Check } from 'lucide-react';
import { DropdownMenu } from 'radix-ui';
import type { ComponentPropsWithRef, ReactNode } from 'react';
import { cn } from '../lib/cn';
import { Shortcut } from './kbd';
import { menuItem, menuSurface } from './menu-styles';

export const Menu = DropdownMenu.Root;
export const MenuTrigger = DropdownMenu.Trigger;
export const MenuRadioGroup = DropdownMenu.RadioGroup;

export function MenuContent({
  className,
  align = 'end',
  sideOffset = 4,
  ...props
}: ComponentPropsWithRef<typeof DropdownMenu.Content>) {
  return (
    <DropdownMenu.Portal>
      <DropdownMenu.Content
        align={align}
        sideOffset={sideOffset}
        collisionPadding={8}
        className={cn(menuSurface, className)}
        {...props}
      />
    </DropdownMenu.Portal>
  );
}

export interface MenuItemProps extends ComponentPropsWithRef<typeof DropdownMenu.Item> {
  icon?: ReactNode;
  shortcut?: readonly string[];
  danger?: boolean;
}

export function MenuItem({
  icon,
  shortcut,
  danger = false,
  children,
  className,
  ...props
}: MenuItemProps) {
  return (
    <DropdownMenu.Item
      className={cn(menuItem, danger && 'text-negative [&_svg]:!text-negative', className)}
      {...props}
    >
      {icon}
      <span className="flex-1 truncate">{children}</span>
      {shortcut && <Shortcut keys={shortcut} />}
    </DropdownMenu.Item>
  );
}

export function MenuLabel({
  className,
  ...props
}: ComponentPropsWithRef<typeof DropdownMenu.Label>) {
  return (
    <DropdownMenu.Label
      className={cn('px-2 pt-1.5 pb-1 text-xs font-medium text-text-3', className)}
      {...props}
    />
  );
}

export function MenuSeparator() {
  return <DropdownMenu.Separator className="-mx-1 my-1 h-px bg-border-subtle" />;
}

export function MenuRadioItem({
  icon,
  children,
  className,
  ...props
}: ComponentPropsWithRef<typeof DropdownMenu.RadioItem> & { icon?: ReactNode }) {
  return (
    <DropdownMenu.RadioItem className={cn(menuItem, 'pr-7', className)} {...props}>
      {icon}
      <span className="flex-1 truncate">{children}</span>
      <DropdownMenu.ItemIndicator className="absolute right-2 inline-flex">
        <Check className="!text-text-1" />
      </DropdownMenu.ItemIndicator>
    </DropdownMenu.RadioItem>
  );
}
